import { Building2, Lock, Sparkles } from 'lucide-react';
import { PageHeader } from '@gs/ui';
import { useAuth, GsRole, hasRole } from '@gs/auth';
import { ALL_APPS, COMING_APPS } from '../data/apps';
import { useFavorites } from '../hooks/useFavorites';
import { AppCard } from '../components/AppCard';
import { ZoneHeader } from '../components/ZoneHeader';
import { PwaInstallButton } from '../components/PwaInstallButton';

const GRID = 'grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(80px,96px))]';

export default function AppsPage() {
  const { toggle, isFav } = useFavorites();
  const { user } = useAuth();
  const isAdmin = hasRole(user, GsRole.ADMIN);

  // Zone 1 — Apps GS métier (non-admin, hors Espace GS)
  const gsApps = ALL_APPS.filter(
    (a) => !a.adminOnly && a.zone !== 'fun' && a.zone !== 'espace-gs',
  );

  // Zone 2 — Administration (admin seulement)
  const adminApps = ALL_APPS.filter((a) => a.adminOnly);

  // Zone 3 — Espace GS (apps fun + espace-gs)
  const funApps = ALL_APPS.filter((a) => a.zone === 'fun' || a.zone === 'espace-gs');

  return (
    <div className="space-y-8 pb-10">
      <PageHeader
        title="Applications"
        description="Accès centralisé à l'écosystème Groupe Signalisation."
        actions={<PwaInstallButton />}
      />

      {/* ── Zone 1 : Applications GS ──────────────────────────────── */}
      <section aria-label="Applications Groupe Signalisation">
        <ZoneHeader
          icon={<Building2 size={16} />}
          title="Groupe Signalisation"
          subtitle="Applications métier de l'entreprise"
          accentColor="brand"
          badge={`${gsApps.length} apps`}
        />
        <div className={GRID}>
          {gsApps.map((app, i) => (
            <AppCard
              key={app.id}
              app={app}
              showStar
              isFav={isFav(app.id)}
              onToggleFav={() => toggle(app.id)}
              index={i}
              userEmail={user?.email}
            />
          ))}
        </div>

        {/* Bientôt disponible */}
        {COMING_APPS.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {COMING_APPS.map((c) => (
              <span
                key={c.id}
                className="rounded-full border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-400"
                title={c.desc}
              >
                {c.name}
              </span>
            ))}
            <span className="flex items-center px-1 text-xs text-slate-400 dark:text-slate-500">
              · Bientôt disponible
            </span>
          </div>
        )}
      </section>

      {/* ── Zone 2 : Administration (admin only) ──────────────────── */}
      {isAdmin && adminApps.length > 0 && (
        <section aria-label="Administration">
          <ZoneHeader
            icon={<Lock size={14} />}
            title="Administration"
            subtitle="Outils réservés aux administrateurs"
            accentColor="blue"
          />
          <div className={GRID}>
            {adminApps.map((app, i) => (
              <AppCard
                key={app.id}
                app={app}
                showStar
                isFav={isFav(app.id)}
                onToggleFav={() => toggle(app.id)}
                index={i}
                userEmail={user?.email}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Zone 3 : Espace GS ─────────────────────────────────── */}
      <section aria-label="Espace GS">
        <ZoneHeader
          icon={<Sparkles size={15} />}
          title="Espace GS"
          subtitle="Applications fun & bien-être pour les employés"
          accentColor="violet"
          badge="Bientôt"
        />
        <div className={GRID}>
          {funApps.map((app, i) => (
            <AppCard
              key={app.id}
              app={app}
              showStar
              isFav={isFav(app.id)}
              onToggleFav={() => toggle(app.id)}
              index={i}
              userEmail={user?.email}
            />
          ))}
        </div>
        <p className="mt-3 text-meta text-slate-400 dark:text-slate-500">
          Ces applications sont en développement — disponibles prochainement pour tous les employés GS.
        </p>
      </section>
    </div>
  );
}
